import { propertyAddressWithUnit } from '@/lib/property-address'
import type { Property } from '@/types'
import type { WizardPropertyOverrides } from './index'

type Translate = (key: string, opts?: Record<string, unknown>) => string

/**
 * Everything the document wizard needs from a property.
 *
 * There are two ways into the wizard — a deal and a property card — and they
 * used to build this object independently. The property card passed three
 * fields to the deal page's ten, so the same office produced `Без НДС` from one
 * entry point and `в том числе НДС 22%` from the other. Which button you
 * pressed decided what the contract said.
 *
 * Rent terms are read from the nested `rentTerms` with a fallback to the flat
 * legacy accessors, except for the VAT triple: those exist only on `rentTerms`,
 * and the flat fields carry no reason, so pairing them would describe terms
 * that were never agreed.
 */
export function toWizardProperty(property: Property, t: Translate): WizardPropertyOverrides {
	const terms = property.rentTerms

	const baseLabel = t(`properties.types.${property.type}`)
	const objectType =
		property.type === 'APARTMENT' && property.rooms
			? `${property.rooms}-к ${baseLabel.toLowerCase()}`
			: baseLabel

	return {
		objectType,
		// With the unit number: both wizard entry points must print the same
		// address, and a contract that stops at the entrance names no premises.
		address:         propertyAddressWithUnit(property, t),
		cadastralNumber: property.cadastralNumber ?? '',
		area:            property.area ?? '',
		rentAmount:      terms?.rentAmount     ?? property.rentAmount,
		rentCurrency:    terms?.rentCurrency   ?? property.rentCurrency,
		depositAmount:   terms?.depositAmount  ?? property.depositAmount,
		// Without this the wizard pins the deposit to the rent currency, and
		// the read-only CurrencyField leaves no way to correct it by hand.
		depositCurrency: terms?.depositCurrency,
		penaltyRate:     terms?.penaltyRate    ?? property.penaltyRate,
		vatPercent:      terms?.vatPercent,
		vatIncluded:     terms?.vatIncluded,
		vatClause:       terms?.vatClause,
	}
}
